/**
 * DissolveModifier — noise-threshold dissolve with an emissive burn edge,
 * applied onto any existing NodeMaterial.
 *
 * Canonical technique (Febucci / Codrops three.js dissolve): sample stable
 * 3D noise in object space, discard fragments whose noise falls below the
 * animated `progress` threshold, and tint the band just above the threshold
 * with an HDR edge colour so the boundary glows into bloom.
 *
 *   noise < progress               → discarded
 *   noise < progress + edgeWidth   → emissive edge band
 *   otherwise                      → base material unchanged
 *
 * `progress` runs 0 → 1 (fully present → fully dissolved). The modifier
 * returns uniforms for per-frame animation; call `dispose()` semantics are
 * the material's own (no GPU resources owned here).
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, positionLocal, mx_noise_float, smoothstep, Discard,
} from 'three/tsl';

export interface DissolveConfig {
  /** Initial progress 0..1 (default 0). */
  progress?: number;
  /** Edge band width in noise units (default 0.08). */
  edgeWidth?: number;
  /** HDR edge colour (default orange 4,1.2,0.2). */
  edgeColor?: [number, number, number];
  /** Object-space noise frequency (default 3). */
  noiseScale?: number;
}

export interface DissolveHandle {
  progress: { value: number };
  edgeWidth: { value: number };
  edgeColor: { value: THREE.Vector3 };
  noiseScale: { value: number };
}

/**
 * CPU mirror of the per-fragment classification (exported for tests):
 * returns 'discard' | 'edge' | 'base'.
 */
export function dissolveClassifyCpu(
  noise: number, progress: number, edgeWidth: number,
): 'discard' | 'edge' | 'base' {
  if (noise < progress) return 'discard';
  if (noise < progress + edgeWidth) return 'edge';
  return 'base';
}

/**
 * Patch `material` with the dissolve. Composes with an existing emissiveNode
 * (edge adds on top). The material is mutated in place (needsUpdate set).
 */
export function applyDissolve(
  material: THREE.Material & {
    emissiveNode?: unknown; opacityNode?: unknown; transparent?: boolean;
  },
  config?: DissolveConfig,
): DissolveHandle {
  const uProgress = uniform(config?.progress ?? 0);
  const uEdgeWidth = uniform(config?.edgeWidth ?? 0.08);
  const ec = config?.edgeColor ?? [4, 1.2, 0.2];
  const uEdgeColor = uniform(vec3(ec[0], ec[1], ec[2]));
  const uNoiseScale = uniform(config?.noiseScale ?? 3);

  // Stable object-space noise: the pattern sticks to the surface as the
  // object moves (positionWorld would make the dissolve "swim").
  const noise = Fn(() => {
    // mx_noise_float returns roughly [-1,1] — remap to [0,1].
    return mx_noise_float(positionLocal.mul(uNoiseScale)).mul(0.5).add(0.5);
  })();

  const prevEmissive = material.emissiveNode;
  material.emissiveNode = Fn(() => {
    const n = noise;
    Discard(n.lessThan(uProgress));
    // Edge band: 1 at the threshold, 0 at threshold+width.
    const edge = float(1).sub(smoothstep(uProgress, uProgress.add(uEdgeWidth), n));
    const base = (prevEmissive ?? vec3(0, 0, 0)) as never;
    return vec3(base).add(uEdgeColor.mul(edge));
  })();
  material.needsUpdate = true;

  return {
    progress: uProgress as unknown as { value: number },
    edgeWidth: uEdgeWidth as unknown as { value: number },
    edgeColor: uEdgeColor as unknown as { value: THREE.Vector3 },
    noiseScale: uNoiseScale as unknown as { value: number },
  };
}
